import CCColors, {updateColorWithOpacity} from '@/Utils/CCColors';
import {isPhone} from '@/Utils/Dimensions';
import {StyleSheet} from 'react-native';

export const styles = StyleSheet.create({
  // Desktop-only outer wrapper: header row on top, stage+workspace body
  // below. Mobile uses mobileContainer instead (untouched).
  pageColumn: {
    flex: 1,
    flexDirection: 'column',
    overflow: 'hidden',
  },
  mobileContainer: {
    flex: 1,
  },
  // No horizontal padding here on purpose - it's a flex:1 + fixed-560 row,
  // same shape as `.body` (stage + workspace) below, so the two rows'
  // fixed-width sections land at the exact same x position and their
  // border-left dividers form one continuous line instead of two
  // independently-padded rows that drift apart by a few px. Each section
  // owns its own inset via headerLeftCluster/workbookHeaderTabsBox instead.
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: updateColorWithOpacity(CCColors.TextColor.Dark, 0.1),
    backgroundColor: CCColors.White,
    // Positioned so it (and the chapter flyout anchored inside it) stacks
    // above `.body`/`.stage` below - see chapterFlyout's zIndex.
    zIndex: 2,
  },
  headerLeftCluster: {
    flex: 1,
    minWidth: 0,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 10,
    paddingHorizontal: 14,
  },
  headerBackBtn: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: updateColorWithOpacity(CCColors.Workbook.Brown, 0.05),
    alignItems: 'center',
    justifyContent: 'center',
  },
  chapterBreadcrumb: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 7,
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 8,
    backgroundColor: updateColorWithOpacity(CCColors.Workbook.Brown, 0.05),
    borderWidth: 1,
    borderColor: 'transparent',
    maxWidth: 380,
    transitionProperty: 'all',
    transitionDuration: '0.15s',
  },
  // Applied on hover and while its dropdown is open, same treatment as
  // participantPillActive below.
  chapterBreadcrumbActive: {
    backgroundColor: 'rgba(250,207,71,0.08)',
    borderColor: 'rgba(165,121,47,0.3)',
  },
  breadcrumbStudyName: {
    fontSize: 13,
    fontWeight: '600',
    color: CCColors.TextColor.Dark,
    flexShrink: 1,
  },
  breadcrumbSep: {
    fontSize: 12,
    color: CCColors.TextColor.Grey,
  },
  breadcrumbChapterName: {
    fontSize: 13,
    color: CCColors.Workbook.Brown,
    flexShrink: 1,
  },
  // Anchored under the breadcrumb's chevron - shows Chapters only (see
  // dropdownHeader below). Participants got split out into its own
  // participantsFlyout, anchored under the participant-count pill instead.
  chapterFlyout: {
    position: 'absolute',
    top: '100%',
    left: 0,
    marginTop: 6,
    width: 300,
    height: 460,
    backgroundColor: CCColors.White,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: updateColorWithOpacity(CCColors.TextColor.Dark, 0.1),
    overflow: 'hidden',
    // Was 30 - too low to reliably beat `.body`/`.stage` below it, so the
    // dropdown rendered underneath the board/workspace instead of over it.
    // Match the rest of the app's "must float over page content" dropdowns
    // (e.g. CCSelect's options list uses 1000+) instead of a small ad-hoc value.
    zIndex: 1000,
    shadowColor: CCColors.Workbook.DarkBrown,
    shadowOffset: {width: 0, height: 4},
    shadowOpacity: 0.12,
    shadowRadius: 16,
    elevation: 6,
  },
  // Chapters/Participants each render a bare `<>` with a `flex: 1`
  // ScrollView inside - no wrapping View of their own. Nesting that through
  // this dropdown's own Box/TouchableOpacity wrappers left `flex: 1` with
  // no reliable height to resolve against (worked when react-native-tab-view
  // owned the sizing; doesn't when we call the scene directly), so the list
  // rendered but at zero height - invisible, not empty. An explicit pixel
  // height sidesteps the flex chain entirely: the flyout's total height
  // (460) minus dropdownHeader's own rendered height above it (~50).
  // (Minus a further 5px for the mini CheckeredStrip under the header.)
  chapterFlyoutBody: {
    height: 405,
  },
  // Shared header chrome for both the chapters and participants dropdowns -
  // a small icon + title on the left, a count pill on the right.
  dropdownHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 14,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: updateColorWithOpacity(CCColors.TextColor.Dark, 0.08),
    backgroundColor: CCColors.Primary.Light,
  },
  dropdownHeaderTitleGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  dropdownHeaderTitle: {
    fontSize: 13,
    fontWeight: '700',
    color: CCColors.TextColor.Primary,
    letterSpacing: 0.2,
  },
  // Count badge as a little dark board square with the gold count on it -
  // matches the ChessModal shell's dark-walnut + gold signature.
  dropdownCountBadge: {
    backgroundColor: CCColors.Workbook.DarkBrown,
    borderRadius: 6,
    paddingHorizontal: 7,
    paddingVertical: 3,
    minWidth: 22,
    alignItems: 'center',
  },
  dropdownCountText: {
    fontSize: 11,
    fontWeight: '700',
    color: CCColors.Primary.Default,
  },
  // Anchored under the participant-count pill (right-aligned since the pill
  // sits mid/right in the header) - shows Participants only.
  participantsFlyout: {
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: 6,
    width: 300,
    height: 460,
    backgroundColor: CCColors.White,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: updateColorWithOpacity(CCColors.TextColor.Dark, 0.1),
    overflow: 'hidden',
    zIndex: 1000,
    shadowColor: CCColors.Workbook.DarkBrown,
    shadowOffset: {width: 0, height: 4},
    shadowOpacity: 0.12,
    shadowRadius: 16,
    elevation: 6,
  },
  participantsFlyoutBody: {
    height: 405,
  },
  // Annotations/Lesson popovers, anchored to their own board-toolbar icon
  // (see renderAnnotationsPopover/renderLessonPopover) and sliding up from
  // it (bottom: '100%') instead of both sharing one flyout anchored under
  // a separate pencil icon in the header.
  boardToolPopover: {
    position: 'absolute',
    bottom: '100%',
    right: 0,
    marginBottom: 8,
    backgroundColor: CCColors.White,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: updateColorWithOpacity(CCColors.TextColor.Dark, 0.1),
    padding: 12,
    zIndex: 1000,
    shadowColor: CCColors.Workbook.DarkBrown,
    shadowOffset: {width: 0, height: 4},
    shadowOpacity: 0.12,
    shadowRadius: 16,
    elevation: 6,
    transitionProperty: 'transform, opacity',
    transitionDuration: '0.18s',
  } as any,
  // Lesson (hint/success/wrong-move) authoring popover: unlike the plain
  // white boardToolPopover box every other flyout uses, this one borrows the
  // chess-branded shell language (dark-walnut header, gold accent, checkered
  // strip - see ChessModalShell/the "Add Chapter" popup) so it visibly reads
  // as a distinct kind of panel from the move-list grid it sits above,
  // rather than just another options dropdown.
  lessonPopover: {
    padding: 0,
    overflow: 'hidden',
    borderColor: updateColorWithOpacity(CCColors.Workbook.DarkBrown, 0.15),
  } as any,
  lessonPopoverHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    backgroundColor: CCColors.Workbook.DarkBrown,
    paddingHorizontal: 14,
    paddingVertical: 12,
  },
  lessonPopoverHeaderIcon: {
    width: 28,
    height: 28,
    borderRadius: 8,
    backgroundColor: 'rgba(250,207,71,0.15)',
    borderWidth: 1,
    borderColor: 'rgba(250,207,71,0.3)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  lessonPopoverBody: {
    backgroundColor: CCColors.Workbook.CreamGold,
    paddingHorizontal: 14,
    paddingTop: 14,
    paddingBottom: 14,
    gap: 12,
  },
  // One tab per guidance field (Success/Hint/Wrong move) - only the active
  // field's input shows below, instead of all three stacked at once.
  lessonTabRow: {
    flexDirection: 'row',
    gap: 6,
  },
  lessonTab: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    paddingVertical: 6,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: updateColorWithOpacity(CCColors.TextColor.Dark, 0.12),
    backgroundColor: CCColors.White,
  },
  lessonTabLabel: {
    fontSize: 10,
    fontWeight: '700',
    color: CCColors.Workbook.Grey,
  },
  lessonHelperText: {
    fontSize: 9.5,
    color: CCColors.Workbook.BrownGrey,
    lineHeight: 14,
  },
  lessonFieldGroup: {
    gap: 5,
  },
  lessonFieldLabelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
  },
  lessonFieldLabel: {
    fontSize: 9.5,
    fontWeight: '700',
    color: CCColors.Workbook.Grey,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
  },
  lessonFieldHint: {
    fontSize: 9.5,
    color: CCColors.Workbook.BrownGrey,
    lineHeight: 13,
  },
  lessonInput: {
    height: 36,
    fontSize: 12,
    color: CCColors.TextColor.Primary,
    backgroundColor: CCColors.White,
    borderRadius: 8,
    paddingHorizontal: 10,
    outlineStyle: 'none',
    borderWidth: 1,
    borderColor: updateColorWithOpacity(CCColors.TextColor.Dark, 0.12),
  } as any,
  // Single line that scrolls horizontally as the coach types past its
  // width, instead of wrapping/growing taller - keeps the popover a fixed,
  // compact height regardless of comment length.
  lessonSingleLineInput: {
    whiteSpace: 'nowrap',
    textAlignVertical: 'center',
  } as any,
  annotationTile: {
    paddingVertical: 6,
    paddingHorizontal: 2,
    minHeight: 46,
    borderRadius: 7,
    borderWidth: 1,
    borderColor: updateColorWithOpacity(CCColors.TextColor.Dark, 0.1),
    backgroundColor: CCColors.White,
    alignItems: 'center',
    justifyContent: 'center',
  },
  // Selected glyph keeps the dark fill but earns a gold ring, echoing the
  // active-state treatment in the create-workbook type cards.
  annotationTileActive: {
    borderColor: CCColors.Primary.Default,
    backgroundColor: CCColors.Primary.Brown,
  },
  headerIconBtn: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  participantPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 12,
    backgroundColor: updateColorWithOpacity(CCColors.Workbook.Brown, 0.05),
    borderWidth: 1,
    borderColor: 'transparent',
    transitionProperty: 'all',
    transitionDuration: '0.15s',
  },
  // Applied on hover and while its dropdown is open.
  participantPillActive: {
    backgroundColor: 'rgba(250,207,71,0.08)',
    borderColor: 'rgba(165,121,47,0.3)',
  },
  body: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'stretch',
    overflow: 'hidden',
  },
  chessboardContainer: {
    flexDirection: 'column',
    width: '100%',
  },
  stage: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FAF6EB',
    padding: 16,
  },
  // The Moves/Analysis/Settings tabs, up in the header (see
  // renderWorkbookHeaderControls in index.tsx) - each tab is flex:1 so they
  // split this box evenly. Same 560 width and border-left as `workspace`
  // below, with zero outer margin, so its divider is pixel-aligned with
  // the workspace's - both rows are a plain flex:1 + fixed-560 split with
  // no extra row-level padding (see `header`'s comment), so the two
  // fixed-width boxes land at the exact same x. Keep this in sync with
  // `workspace.width` and WORKSPACE_WIDTH.
  workbookHeaderTabsBox: {
    width: isPhone ? '100%' : 560,
    flexShrink: 0,
    height: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 10,
    backgroundColor: '#FAF6EB',
    borderLeftWidth: 1,
    borderLeftColor: updateColorWithOpacity(CCColors.TextColor.Dark, 0.1),
  },
  // One "Workbook" panel (Moves/Analysis tabs) instead of the old Chapter/
  // Worktree column pair - kept the same overall width the pair used to
  // split between them, so the scoresheet-style move list and the eval/
  // explorer views both get generous room. Kept in sync with
  // WORKSPACE_WIDTH in index.tsx, which subtracts this same number when
  // sizing the board.
  workspace: {
    width: isPhone ? '100%' : 560,
    flexShrink: 0,
    backgroundColor: CCColors.White,
    borderLeftWidth: 1,
    borderLeftColor: 'rgba(38,35,34,0.1)',
    height: '100%',
    flexDirection: 'row',
  },
  workspaceColumn: {
    flex: 1,
    height: '100%',
  },
  workspaceTabRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    padding: 8,
  },
  // Sized to content (not flex:1) so Moves/Analysis read as a compact
  // segmented control rather than two buttons stretched across the whole
  // row - the chat/settings icons share the same row now, pushed to the
  // far right by the spacer Box next to them.
  workspaceTabBtn: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 6,
    paddingVertical: 7,
    paddingHorizontal: 14,
    borderRadius: 8,
    alignItems: 'center',
    transitionProperty: 'background-color',
    transitionDuration: '0.15s',
  } as any,
  workspaceTabBtnActive: {
    backgroundColor: 'rgba(250,207,71,0.1)',
    borderWidth: 1,
    borderColor: 'rgba(165,121,47,0.2)',
  },
  workspaceTabLabel: {
    fontSize: 12,
    fontWeight: '500',
    color: CCColors.Workbook.Grey,
  },
  workspaceTabLabelActive: {
    color: '#A5792F',
    fontWeight: '600',
  },
  // The Settings tab's Chapter/Share & export switcher - a solid-fill pill
  // (rather than the flat white/teal look of the outer Moves/Analysis/
  // Settings tabs) so this nested level of navigation reads as visually
  // subordinate to, and distinct from, the tabs above it. Fill is the
  // app's own primary accent (CCColors.Primary.Default, the same yellow
  // CCButton uses) rather than a one-off dark brown, so it actually
  // matches the rest of the app instead of introducing a new color.
  settingsSubTabPill: {
    flexDirection: 'row',
    backgroundColor: '#FAF6EB',
    borderRadius: 10,
    padding: 3,
    gap: 3,
    borderWidth: 1,
    borderColor: '#EAE2D5',
  },
  settingsSubTabBtn: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingVertical: 8,
    borderRadius: 8,
    transitionProperty: 'background-color',
    transitionDuration: '0.15s',
  } as any,
  settingsSubTabBtnActive: {
    backgroundColor: CCColors.Primary.Default,
    shadowColor: '#FACF47',
    shadowOffset: {width: 0, height: 1},
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 2,
  },
  settingsSubTabLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: CCColors.Workbook.Brown,
  },
  settingsSubTabLabelActive: {
    color: CCColors.TextColor.Primary,
  },
  // A grouped settings list (native-Settings-app style): a bordered white
  // card containing one or more settingsRow entries separated by
  // settingsRowDivider hairlines, under a small uppercase settingsGroupLabel
  // eyebrow - used by the Share & export sub-tab.
  settingsGroupLabel: {
    fontSize: 11,
    fontWeight: '700',
    color: CCColors.Workbook.Grey,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 6,
    marginLeft: 2,
  },
  settingsGroup: {
    backgroundColor: CCColors.White,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: updateColorWithOpacity(CCColors.TextColor.Dark, 0.1),
    overflow: 'hidden',
  },
  settingsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    padding: 12,
  },
  settingsRowDivider: {
    height: 1,
    backgroundColor: updateColorWithOpacity(CCColors.TextColor.Dark, 0.08),
    marginLeft: 12,
  },
  settingsRowIcon: {
    width: 30,
    height: 30,
    borderRadius: 8,
    backgroundColor: updateColorWithOpacity(CCColors.TextColor.Primary, 0.08),
    alignItems: 'center',
    justifyContent: 'center',
  },
  commentCard: {
    width: '100%',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: updateColorWithOpacity(CCColors.TextColor.Dark, 0.1),
    backgroundColor: CCColors.White,
    padding: 10,
  },
  commentCardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  commentCardLabel: {
    fontSize: 10.5,
    fontWeight: '700',
    color: CCColors.TextColor.Grey,
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
  addNewBox: {
    backgroundColor: CCColors.Primary.Brown,
    padding: 10,
    borderRadius: 8,
  },
  disabled: {
    backgroundColor: CCColors.Grey.Shade200,
    padding: 10,
    borderRadius: 8,
  },
  boardControls: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    marginTop: 12,
  },
  boardControlsLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  boardControlsRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  controlIcon: {
    width: 34,
    height: 34,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  infoHintCard: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 7,
    padding: 10,
    borderRadius: 10,
    backgroundColor: '#FAF6EB',
    borderWidth: 1,
    borderColor: '#EAE2D5',
    margin: 12,
    marginBottom: 0,
  },
  movesPanelHeader: {
    padding: 12,
    paddingBottom: 0,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(38,35,34,0.1)',
  },
  evalRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 10,
    paddingTop: 8,
  },
  stickyButtonWrapper: {
    padding: 14,
    paddingTop: 10,
    backgroundColor: CCColors?.White,
    borderTopWidth: 1,
    borderTopColor: updateColorWithOpacity(CCColors.TextColor.Dark, 0.1),
  },
  syncRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 8,
    paddingHorizontal: 4,
    borderTopWidth: 1,
    borderTopColor: updateColorWithOpacity(CCColors.TextColor.Dark, 0.06),
    marginTop: 8,
  },
});
